<template>
  <dialog
    v-if="appointment?.id"
    ref="contextMenu"
    :style="getPositionStyle"
    class="context-menu d-flex flex-column"
    @click.stop
    @mousedown.stop>
    <div class="d-flex justify-space-between">
      <div class="pa-4 text-black">
        <span class="d-flex flex-row align-center">
          <v-icon color="black" size="x-small">mdi-truck</v-icon>
          <span class="d-inline-block ml-2">{{ appointment.companyName }}</span>
        </span>
        <span class="d-block">
          <strong>{{ appointment.refNumLabel }}</strong>
          : {{ appointment.refNumber }}
        </span>
      </div>
      <v-btn variant="text" icon absolute class="mt-2" @click.stop="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <template v-if="appointment.status !== novaCore.AppointmentStatus.Cancelled">
      <v-divider></v-divider>
      <div class="pt-4 pr-4 pb-1 pl-4 text-black">
        <span class="sub-title text-left d-block">Change Status To</span>
      </div>
      <status-manager-compact
        @status-change="$emit('close')"
        :appointment="appointment"
        mixpanel-entry-point="Quick Status Change"
        :warehouse-triggers="$selectedWarehouseTriggers"
        dense
        compact
        @close="$emit('close')"
        class="mb-2"></status-manager-compact>
      <v-divider></v-divider>
      <template v-if="canCancel || isCancelled">
        <v-btn
          v-if="!isCancelled && $rolePermissions.canUpdateAppointment"
          elevation="0"
          class="text__error font-weight-bold my-2"
          color="white"
          @click="confirmCancel"
          :disabled="appointment.status === novaCore.AppointmentStatus.Cancelled">
          <v-icon class="mr-2 colored">{{ cancelButtonIcon }}</v-icon>
          {{ cancelAppointmentLabel }}
        </v-btn>
      </template>
      <cancel-appointment-dialog-old
        ref="cancelAppointment"
        @confirm="$emit('confirm')"
        mixpanel-entry-point="Quick Status Change"
        :external-activator="true"
        :show-dialog="showConfirmDialog"
        :appointment="appointment"
        @close="showConfirmDialog = false"></cancel-appointment-dialog-old>
    </template>
  </dialog>
</template>

<script>
import appointmentMixin from '@/components/mixins/appointmentMixin';

/**
 * @displayName Event Context Menu
 */
export default {
  name: 'EventContextMenu',
  mixins: [appointmentMixin],
  props: {
    /**
     * Event object named 'appointment' to work with appointment mixin
     */
    appointment: {
      type: Object,
      required: true
    },
    nativeEvent: {
      type: PointerEvent,
      required: false
    }
  },
  data() {
    return {
      showConfirmDialog: false
    };
  },
  computed: {
    selfHeight() {
      return this.$refs.contextMenu?.clientHeight || 240;
    },
    selfWidth() {
      return this.$refs.contextMenu?.clientWidth || 230;
    },
    /*
     * Returns true if the user clicked on the last third of the screen
     */
    isEventNearBottomOfScreen() {
      const clickYPos = this.nativeEvent?.pageY;
      const windowBottom = window.innerHeight || 600;

      return clickYPos && this.selfHeight + clickYPos > windowBottom;
    },
    /*
     * Returns true if the user clicked on the last third of the screen
     */
    isEventNearRightOfScreen() {
      const clickXPos = this.nativeEvent?.pageX;
      const windowRight = window.innerWidth || 800;

      return clickXPos && this.selfWidth + clickXPos > windowRight;
    },
    eventEl() {
      return document
        .querySelector(`.event-content[data-event-id="${this.appointment.id}"]`)
        .closest('.fc-timegrid-event-harness');
    },
    /*
     * Draws the context menu attaching from the bottom or the top of the event based where the user clicked
     */
    getPositionStyle() {
      const style = {
        top: `${this.nativeEvent.pageY}px`,
        left: `${this.nativeEvent.pageX}px`
      };

      if (this.isEventNearBottomOfScreen) {
        style.top = `${this.nativeEvent.pageY - this.selfHeight}px`;
      }

      if (this.isEventNearRightOfScreen) {
        style.left = `${this.nativeEvent.pageX - this.selfWidth}px`;
      }

      return style;
    }
  },
  methods: {
    handleOutsideClick(e) {
      const targetElement = e.target;
      if (!targetElement.closest('.context-menu') && !targetElement.closest('.v-dialog')) {
        this.$emit('close');
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  }
};
</script>

<style scoped lang="scss">
.context-menu {
  position: fixed;
  box-shadow: 5px 5px 5px rgba(0, 0, 0, 0.2);
  min-width: 200px;
  background-color: $white;
  border-radius: 5px;
  z-index: 20;
  border: none;

  &:hover {
    cursor: initial;
  }

  .top {
    border-bottom: 1px solid rgba(0, 0, 0, 0.2);
  }

  .label {
    text-transform: uppercase;
    color: grey;
    font-weight: bold;
  }
}
</style>
